import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLanguage } from '@/hooks/use-language';
import { useEvents } from '@/hooks/use-events';
import { useAuth } from '@/hooks/use-auth';
import { useGroups } from '@/hooks/use-groups';
import { useNotifications } from '@/hooks/use-notifications';
import type { ParticipantDetails } from '@/contexts/event/EventContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Users, Clock, MapPin, Phone, UserX, Target, UserCheck, Dumbbell, Coffee, Trophy, Pencil, Repeat, Bell } from 'lucide-react';
import { EditEventDialog } from '@/components/admin/EditEventDialog';
import { RecurringEventManager } from '@/components/admin/RecurringEventManager';
import { EventComments } from '@/components/events/EventComments';
import { getGroupColorClasses, getLocalizedGroupName } from '@/utils/groupColors';
import { formatEventTime } from '@/utils/dateUtils';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { events, isLoading, removeParticipant, refreshEvents, signUp, cancel, joinWaitingList, leaveWaitingList } = useEvents();
  const { user } = useAuth();
  const { allGroups } = useGroups();
  const { addNotification } = useNotifications();
  const supabase = useSupabase();
  
  const isAdmin = user?.isAdmin || false;

  const eventId = id || '0';
  const event = events.find(e => e.id === eventId);
  
  const defaultTournamentType = useMemo(() => {
    if (!event) return 'mexicano'; // Changed default to mexicano (most common)
    const title = event.titleKey ? t(event.titleKey) : event.title;
    const description = event.descriptionKey ? t(event.descriptionKey) : event.description;
    const combined = `${title} ${description}`.toLowerCase();
    
    // Check for mexicano first (most specific)
    if (combined.includes('mexicano')) return 'mexicano';
    
    // Then check for team americano (must include both "team" AND "americano")
    if (combined.includes('team') && combined.includes('americano')) return 'team_americano';
    
    // Check for just americano
    if (combined.includes('americano')) return 'americano';
    
    // Default to mexicano (most common tournament type)
    return 'mexicano';
  }, [event, t]);

  const [selectedTournamentType, setSelectedTournamentType] = useState<'americano' | 'mexicano' | 'team_americano'>(defaultTournamentType);
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  const [seriesManagerOpen, setSeriesManagerOpen] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showSignupTimes, setShowSignupTimes] = useState(false);
  const [isSendingReminders, setIsSendingReminders] = useState(false);

  useEffect(() => {
    setSelectedTournamentType(defaultTournamentType);
  }, [defaultTournamentType]);

  const participantDetails = useMemo<ParticipantDetails[]>(() => {
    if (!event) return [];
    if (event.participantDetails && event.participantDetails.length > 0) {
      return event.participantDetails;
    }
    return event.participants.map((participantId, index) => ({
      id: participantId,
      name: `Player ${index + 1}`,
      phone: '',
      groups: [],
    }));
  }, [event]);

  const participantCount = participantDetails.length;
  const canStartTournament = participantCount >= 4;
  const missingCount = Math.max(0, 4 - participantCount);

  const unansweredMembers = useMemo(() => {
    if (!event?.group || !allGroups.length) return [];

    const eventGroup = allGroups.find(g => g.id === event.group || g.name === event.group);
    if (!eventGroup?.members) return [];

    const respondedIds = new Set([
      ...event.participants,
      ...(event.waitingList || [])
    ]);

    return eventGroup.members.filter(member => !respondedIds.has(member.id));
  }, [event, allGroups]);

  // Auto-switch away from team_americano if participant count becomes odd
  useEffect(() => {
    if (selectedTournamentType === 'team_americano' && participantCount % 2 !== 0) {
      setSelectedTournamentType('americano');
    }
  }, [participantCount, selectedTournamentType]);

  // State for tracking participant pending removal
  const [participantPendingRemoval, setParticipantPendingRemoval] = useState<string | null>(null);
  
  // Scroll to top when component mounts or when navigated to
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [eventId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">{t('event.not_found.title')}</h2>
          <p className="text-muted-foreground">
            {t('event.not_found.description')}
          </p>
          <Button onClick={() => navigate('/')} className="mt-4">
            {t("nav.back")}
          </Button>
        </div>
      </div>
    );
  }

  const handleRemoveParticipantClick = (participantId: string) => {
    if (participantPendingRemoval === participantId) {
      // Second click - actually remove the participant
      removeParticipant(event.id, participantId);
      setParticipantPendingRemoval(null);
    } else {
      // First click - set as pending removal
      setParticipantPendingRemoval(participantId);
      // Auto-cancel after 5 seconds
      setTimeout(() => {
        setParticipantPendingRemoval(null);
      }, 5000);
    }
  };

  const formatDate = (date: Date) => {
    const locale = language === 'no' ? 'nb-NO' : 'en-US';
    return date.toLocaleDateString(locale, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getEventIcon = () => {
    switch (event.type) {
      case 'training':
        return <Dumbbell className="h-5 w-5 text-blue-600" />;
      case 'social':
        return <Coffee className="h-5 w-5 text-purple-600" />;
      default:
        return <Target className="h-5 w-5 text-gray-600" />;
    }
  };

  const getEventTypeLabel = () => {
    switch (event.type) {
      case 'training':
        return 'Training Session';
      case 'social':
        return 'Social Event';
      default:
        return 'Event';
    }
  };

  // Check current user's participation status
  const userId = user?.id || '';
  const isParticipant = event ? event.participants.includes(userId) : false;
  const isOnWaitingList = event && event.waitingList ? event.waitingList.includes(userId) : false;
  const eventIsFull = event ? event.participants.length >= event.maxParticipants : false;

  const handleJoinEvent = async () => {
    if (!user) {
      toast.error(t("auth.required"));
      return;
    }
    if (!event) return;
    
    setIsJoining(true);
    try {
      if (eventIsFull) {
        // Join waiting list instead
        await joinWaitingList(event.id, userId);
      } else {
        await signUp(event.id, userId);
      }
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeaveEvent = async () => {
    if (!user || !event) return;
    setIsLeaving(true);
    try {
      await cancel(event.id, userId);
    } finally {
      setIsLeaving(false);
    }
  };

  const handleLeaveWaitingList = async () => {
    if (!user || !event) return;
    setIsLeaving(true);
    try {
      await leaveWaitingList(event.id, userId);
    } finally {
      setIsLeaving(false);
    }
  };

  const handleStartTournament = () => {
    // Validate team_americano requires even number of players
    if (selectedTournamentType === 'team_americano' && participantCount % 2 !== 0) {
      toast.error(t('event.team_americano_error'));
      return;
    }

    const routePath = selectedTournamentType === 'mexicano' ? '/mexicano' : '/americano';
    navigate(routePath, {
      state: {
        participantDetails,
        fromEventId: event.id,
        tournamentType: selectedTournamentType
      }
    });
  };

  const handleSendReminders = async () => {
    if (!event || unansweredMembers.length === 0) return;

    setIsSendingReminders(true);
    try {
      // Create notifications for each unanswered member
      const promises = unansweredMembers.map(member =>
        addNotification({
          userId: member.id,
          type: 'event',
          title: t('event.reminder_title') || 'Event Reminder',
          description: `${event.titleKey ? t(event.titleKey) : event.title} - ${formatDate(event.date)} at ${formatEventTime(event.time)}`,
          priority: 'medium',
          actionUrl: `/event/${event.id}`,
          metadata: {
            eventId: event.id,
            notificationId: `event_reminder_${event.id}_${member.id}`
          }
        })
      );

      await Promise.all(promises);
      toast.success((t('event.reminders_sent') || 'Reminders sent to {count} members').replace('{count}', String(unansweredMembers.length)));
    } catch (error) {
      console.error('Failed to send reminders:', error);
      toast.error(t('event.reminders_error') || 'Failed to send reminders');
    } finally {
      setIsSendingReminders(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("nav.back")}
          </Button>
        </div>
        
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
              {event.titleKey ? t(event.titleKey) : event.title}
            </h1>
            {event.parentEventId && (
              <Badge variant="outline" className="text-sm">
                <Repeat className="h-3 w-3 mr-1" />
                {t('event.recurring_badge')}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-2">
            {event.descriptionKey ? t(event.descriptionKey) : event.description}
          </p>
        </div>
        
        {isAdmin && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {event.parentEventId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSeriesManagerOpen(true)}
                className="w-full sm:w-auto"
              >
                <Repeat className="h-4 w-4 mr-2" />
                {t('admin.view_series')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditEventDialogOpen(true)}
              className="w-full sm:w-auto"
            >
              <Pencil className="h-4 w-4 mr-2" />
              {t('admin.edit_event')}
            </Button>
            <LoadingButton
              variant="outline"
              size="sm"
              onClick={handleSendReminders}
              loading={isSendingReminders}
              disabled={unansweredMembers.length === 0}
              className="w-full sm:w-auto"
            >
              <Bell className="h-4 w-4 mr-2" />
              {t('admin.send_reminder')} ({unansweredMembers.length})
            </LoadingButton>
          </div>
        )}
      </div>

      {/* Event Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {getEventIcon()}
            {t('event.details')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t('event.date_time')}</span>
              <span>{formatDate(event.date)} at {formatEventTime(event.time)} - {formatEventTime(
                new Date(new Date(`2000-01-01T${event.time}`).getTime() + 90 * 60000).toTimeString().slice(0, 5)
              )}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t('event.location_label')}</span>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location.split('·')[0].trim())}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
              >
                {event.location.split('·')[0].trim()}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t('event.participants_label')}</span>
              <Badge variant="secondary">
                {event.participants.length} / {event.maxParticipants}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t('event.group_label')}</span>
              <span>{event.groupKey ? t(event.groupKey) : event.group}</span>
            </div>
          </div>
          
          {event.notes && (
            <div className="mt-4">
              <Separator className="mb-4" />
              <div>
                <span className="font-medium">{t('event.notes_label')}</span>
                <p className="text-muted-foreground mt-1">{event.notes}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Join/Leave Event Actions */}
      {user && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {isParticipant ? (
                  <>
                    <UserCheck className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium">{t('event.signed_up')}</p>
                      <p className="text-sm text-muted-foreground">
                        {participantCount} / {event.maxParticipants} {t('event.participants_count')}
                      </p>
                    </div>
                  </>
                ) : isOnWaitingList ? (
                  <>
                    <Clock className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium">{t('event.waiting_list')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('event.full')} ({event.maxParticipants} {t('event.participants_count')})
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">
                        {eventIsFull ? t('event.full') : t('event.join')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {participantCount} / {event.maxParticipants} {t('event.participants_count')}
                        {eventIsFull && t('event.join_waiting_list_info')}
                      </p>
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                {isParticipant ? (
                  <LoadingButton
                    variant="destructive"
                    size="sm"
                    onClick={handleLeaveEvent}
                    loading={isLeaving}
                  >
                    <UserX className="h-4 w-4 mr-2" />
                    {t('event.leave')}
                  </LoadingButton>
                ) : isOnWaitingList ? (
                  <LoadingButton
                    variant="outline"
                    size="sm"
                    onClick={handleLeaveWaitingList}
                    loading={isLeaving}
                  >
                    <UserX className="h-4 w-4 mr-2" />
                    {t('event.leave_waiting_list')}
                  </LoadingButton>
                ) : (
                  <LoadingButton
                    variant="default"
                    size="sm"
                    onClick={handleJoinEvent}
                    loading={isJoining}
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    {eventIsFull ? t('event.join_waiting_list_button') : t('event.join_button')}
                  </LoadingButton>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participants */}
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 space-y-1">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <span>{t('event.signed_up_participants')}</span>
                <Badge variant="secondary">
                  {participantCount} {t('event.participants_count')}
                </Badge>
              </CardTitle>
              {isAdmin && participantDetails.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSignupTimes(!showSignupTimes)}
                  className="flex items-center gap-2"
                >
                  <Clock className="h-4 w-4" />
                  {showSignupTimes ? 'Hide Times' : 'Show Times'}
                </Button>
              )}
            </div>
            {participantDetails.length > 0 && (
              <CardDescription>
                {t('event.signup_description')}
              </CardDescription>
            )}
          </div>
          {isAdmin && canStartTournament && (
            <div className="flex flex-col gap-2 w-full sm:w-auto">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">{t('event.tournament_type')}</label>
                <Select value={selectedTournamentType} onValueChange={(value: 'americano' | 'mexicano' | 'team_americano') => setSelectedTournamentType(value)}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder={t('event.select_tournament_type')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="americano">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-green-500" />
                        {t('event.americano')}
                      </div>
                    </SelectItem>
                    <SelectItem value="mexicano">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-blue-500" />
                        {t('event.mexicano')}
                      </div>
                    </SelectItem>
                    <SelectItem value="team_americano" disabled={participantCount % 2 !== 0}>
                      <div className="flex items-center gap-2">
                        <UserCheck className={`h-4 w-4 ${participantCount % 2 !== 0 ? 'text-muted-foreground' : 'text-purple-500'}`} />
                        {t('event.team_americano')} {participantCount % 2 !== 0 && t('event.needs_even_players')}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {participantCount % 2 !== 0 && selectedTournamentType === 'team_americano' && (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  {t('event.team_americano_error')}. {t('common.current')}: {participantCount}
                </p>
              )}
              <Button 
                onClick={handleStartTournament} 
                className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                disabled={selectedTournamentType === 'team_americano' && participantCount % 2 !== 0}
              >
                <Trophy className="h-4 w-4 mr-2" />
                {t('event.start_tournament')}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {participantDetails.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t('event.no_participants')}</p>
              <p className="text-sm">{t('event.be_first')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {participantDetails.map((participant, index) => (
                <div key={participant.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{participant.name}</div>
                      {participant.isAdmin && (
                        <Badge variant="admin" className="text-xs">
                          {t('common.admin')}
                        </Badge>
                      )}
                      {/* Color dots for group membership */}
                      {participant.groups && participant.groups.length > 0 && (
                        <div className="flex items-center gap-2">
                          {(() => {
                            const [first] = participant.groups;
                            const colors = getGroupColorClasses(first);
                            const localized = getLocalizedGroupName(first, language);
                            return (
                              <Badge 
                                variant="outline" 
                                className={cn("text-xs flex items-center gap-1 border-transparent", colors.bg, colors.text)}
                              >
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: `hsl(var(--group-${colors.variant}))` }}
                                />
                                {localized}
                              </Badge>
                            );
                          })()}
                          {participant.groups.slice(1).map((group) => {
                            const colors = getGroupColorClasses(group);
                            const localizedGroupName = getLocalizedGroupName(group, language);
                            return (
                              <Tooltip key={group}>
                                <TooltipTrigger asChild>
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: `hsl(var(--group-${colors.variant}))` }}
                                  />
                                </TooltipTrigger>
                                <TooltipContent>{localizedGroupName}</TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {participant.phone}
                    </div>
                    {showSignupTimes && participant.joinedAt && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(participant.joinedAt), 'MMM d, HH:mm:ss.SSS')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      #{index + 1}
                    </Badge>
                    {isAdmin && (
                      <Button
                        variant={participantPendingRemoval === participant.id ? "destructive" : "outline"}
                        size="sm"
                        className={
                          participantPendingRemoval === participant.id 
                            ? "bg-red-600 text-white hover:bg-red-700 px-4" 
                            : "text-destructive hover:text-destructive"
                        }
                        onClick={() => handleRemoveParticipantClick(participant.id)}
                      >
                        {participantPendingRemoval === participant.id ? (
                          <span className="flex items-center gap-1">
                            <UserX className="h-4 w-4" />
                            Confirm Remove
                          </span>
                        ) : (
                          <UserX className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>


      {/* Waiting List */}
      {event.waitingListDetails && event.waitingListDetails.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('event.waiting_list_title')}</CardTitle>
            <CardDescription>
              {event.waitingListDetails.length} {t('event.participants_count')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {event.waitingListDetails.map((participant, index) => (
                <div key={participant.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{participant.name}</div>
                      {participant.isAdmin && (
                        <Badge variant="admin" className="text-xs">
                          {t('common.admin')}
                        </Badge>
                      )}
                      {/* Color dots for group membership */}
                      {participant.groups && participant.groups.length > 0 && (
                        <div className="flex items-center gap-2">
                          {(() => {
                            const [first] = participant.groups;
                            const colors = getGroupColorClasses(first);
                            const localized = getLocalizedGroupName(first, language);
                            return (
                              <Badge 
                                variant="outline" 
                                className={cn("text-xs flex items-center gap-1 border-transparent", colors.bg, colors.text)}
                              >
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: `hsl(var(--group-${colors.variant}))` }}
                                />
                                {localized}
                              </Badge>
                            );
                          })()}
                          {participant.groups.slice(1).map((group) => {
                            const colors = getGroupColorClasses(group);
                            const localizedGroupName = getLocalizedGroupName(group, language);
                            return (
                              <Tooltip key={group}>
                                <TooltipTrigger asChild>
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: `hsl(var(--group-${colors.variant}))` }}
                                  />
                                </TooltipTrigger>
                                <TooltipContent>{localizedGroupName}</TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {participant.phone}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Position #{index + 1}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Event Dialog */}
      {isAdmin && (
        <>
          <EditEventDialog
            open={editEventDialogOpen}
            onOpenChange={setEditEventDialogOpen}
            event={event}
            onEventUpdated={() => { refreshEvents(); }}
            onEventDeleted={() => {
              refreshEvents();
              navigate('/');
            }}
          />
          {event.parentEventId && (
            <RecurringEventManager
              open={seriesManagerOpen}
              onOpenChange={setSeriesManagerOpen}
              seriesId={event.parentEventId}
              onSeriesUpdated={() => { refreshEvents(); }}
            />
          )}
        </>
      )}

      {/* Comments Section */}
      {event && (
        <div className="mt-6">
          <EventComments eventId={event.id} />
        </div>
      )}
    </div>
  );
}
